<template>
	<div class="dropdown" ref="dropdown" :style="computedStyle">
		<div class="dropdown-selected"
			:class="{ 'has-icon': showIcons && selectedItemIcon, 'has-tag': showTags && selectedItemTag }"
			@click="toggleOpen">
			<img v-if="showIcons && selectedItemIcon" :src="selectedItemIcon" class="dropdown-item-icon" alt="" />
			<input class="dropdown-filter" type="text" v-model="searchTerm" :placeholder="selectedItemName"
				@input="updateFilter" @keydown="handleKeydown" :data-selected-id="selectedItem" />
			<span v-if="showTags && selectedItemTag" 
				:class="props.compactTag ? 'dropdown-tag-dot' : 'dropdown-tag'"
				:style="props.compactTag ? { backgroundColor: selectedItemTag.color } : { backgroundColor: selectedItemTag.bgColor, color: selectedItemTag.color }">
				{{ !props.compactTag ? selectedItemTag.label : '' }}
			</span>
			<i :class="['dropdown-icon', isOpen ? 'icon-up' : 'icon-down']" v-if="options.length > 1"></i>
		</div>
		<div v-show="isOpen" class="dropdown-options">
			<div class="dropdown-options-item" ref="options">
				<div v-for="(item, index) in filteredOptions" :key="item.id">
					<div class="item" :data-id="item.id"
						:class="{ 'dropdown-option-selected': selectedItem === item.id, 'dropdown-option-highlighted': highlightedIndex === index }"
						@click="selectOption(item)">
						<img v-if="showIcons && getIconForItem(item.id)" :src="getIconForItem(item.id)"
							class="dropdown-item-icon options-item-icon" alt="" />
						<span class="item-name">{{ item.name }}</span>
						<span v-if="showTags && getTagForItem(item)" 
							:class="props.compactTag ? 'dropdown-tag-dot options-tag-dot' : 'dropdown-tag options-tag'"
							:style="props.compactTag ? { backgroundColor: getTagForItem(item).color } : { backgroundColor: getTagForItem(item).bgColor, color: getTagForItem(item).color }">
							{{ !props.compactTag ? getTagForItem(item).label : '' }}
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, watchEffect, onMounted, onUnmounted, getCurrentInstance, nextTick } from 'vue';
const { emit } = getCurrentInstance();

const props = defineProps({
	options: {
		type: Array,
		required: true,
	},
	selected: {
		type: [String, Number],
		default: null,
	},
	width: {
		type: String,
		default: '100%',
	},
	minWidth: {
		type: String,
		default: null,
	},
	maxWidth: {
		type: String,
		default: null,
	},
	showIcons: {
		type: Boolean,
		default: false,
	},
	iconMap: {
		type: Object,
		default: () => ({}),
	},
	showTags: {
		type: Boolean,
		default: false,
	},
	getTagForItem: {
		type: Function,
		default: null,
	},
	compactTag: {
		type: Boolean,
		default: false,
	},
});

const selectedItem = ref(props.selected);
const isOpen = ref(false);
const highlightedIndex = ref(-1);
const searchTerm = ref('');
const dynamicWidth = ref(null);

function normalizeForSearch(value) {
	return (value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/œ/g, 'oe')
		.replace(/æ/g, 'ae')
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '');
}

watchEffect(() => {
	selectedItem.value = props.selected;
});

const selectedItemName = computed(() => {
	const selectedOption = props.options.find(option => option.id == selectedItem.value);
	return selectedOption ? selectedOption.name : '';
});

const selectedItemIcon = computed(() => {
	if (!props.showIcons || !selectedItem.value) return null;
	return props.iconMap[selectedItem.value] || null;
});

const selectedItemTag = computed(() => {
	if (!props.showTags || !selectedItem.value || !props.getTagForItem) return null;

	const selectedOption = props.options.find(option => option.id == selectedItem.value);
	if (!selectedOption) return null;

	return props.getTagForItem(selectedOption);
});

const baseWidthPx = computed(() => {
	if (!props.width) return 280;
	const match = props.width.match(/(\d+(?:\.\d+)?)px/);
	return match ? parseFloat(match[1]) : 280;
});

const computedWidth = computed(() => {
	if (dynamicWidth.value && dynamicWidth.value > baseWidthPx.value) {
		return `${dynamicWidth.value}px`;
	}
	return props.width;
});

const computedStyle = computed(() => {
	const style = { width: computedWidth.value };
	if (props.minWidth) {
		style.minWidth = props.minWidth;
	}
	if (props.maxWidth) {
		style.maxWidth = props.maxWidth;
	}
	return style;
});

function calculateRequiredWidth() {
	if (!selectedItemName.value) {
		dynamicWidth.value = null;
		return;
	}

	nextTick(() => {
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');

		context.font = '500 13px sans-serif';
		const textWidth = context.measureText(selectedItemName.value).width;

		const basePadding = 24;
		const gap = 8;

		const iconWidth = props.showIcons && selectedItemIcon.value ? 20 + gap + 8 : 0;

		let tagWidth = 0;
		if (props.showTags && selectedItemTag.value) {
			if (props.compactTag) {
				tagWidth = 10 + gap;
			} else {
				context.font = '600 12px sans-serif';
				const tagTextWidth = context.measureText(selectedItemTag.value.label).width;
				tagWidth = tagTextWidth + 24 + gap;
			}
		}

		const dropdownIconWidth = props.options.length > 1 ? 12 + 24 : 0;

		const inputPadding = 16;

		const extraRightPadding = 16;

		const requiredWidth = textWidth + basePadding + iconWidth + tagWidth + dropdownIconWidth + inputPadding + extraRightPadding;

		if (requiredWidth > baseWidthPx.value) {
			dynamicWidth.value = Math.ceil(requiredWidth);
		} else {
			dynamicWidth.value = null;
		}
	});
}

function getIconForItem(itemId) {
	if (!props.showIcons) return null;
	return props.iconMap[itemId] || null;
}

function getTagForItem(item) {
	if (!props.showTags || !props.getTagForItem) return null;
	return props.getTagForItem(item);
}

const filteredOptions = computed(() => {
	const term = normalizeForSearch(searchTerm.value);
	const options = props.options.filter(option => {
		const name = normalizeForSearch(option.name);
		return name.includes(term);
	});

	highlightedIndex.value = -1;

	return options;
});

function toggleOpen() {
	if (props.options.length <= 1) {
		return;
	}

	if (isOpen.value) {
		isOpen.value = false;
	} else {
		isOpen.value = true;
		const index = filteredOptions.value.findIndex(option => option.id === selectedItem.value);
		highlightedIndex.value = index !== -1 ? index : -1;
	}
}

function selectOption(item) {
	selectedItem.value = item.id;
	isOpen.value = false;
	searchTerm.value = '';
	emit('changeItem', item.id);
}

function handleArrowKey(direction) {
	if (props.options.length <= 1) {
		return;
	}

	if (!isOpen.value) {
		isOpen.value = true;
	}

	let step;
	if (direction === 'down') {
		step = 1; // Move down by 1
	} else if (direction === 'up') {
		step = -1; // Move up by 1
	} else if (direction === 'right') {
		step = 5; // Move right by 5
	} else if (direction === 'left') {
		step = -5; // Move left by 5
	}

	highlightedIndex.value = (highlightedIndex.value + step + filteredOptions.value.length) % filteredOptions.value.length;

	scrollToHighlightedItem();
}

function scrollToHighlightedItem() {
	const optionsElement = document.querySelectorAll('.dropdown-options .item')[highlightedIndex.value];
	if (optionsElement) {
		optionsElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
	}
}

function handleEnterKey() {
	if (highlightedIndex.value >= 0 && highlightedIndex.value < filteredOptions.value.length) {
		selectOption(filteredOptions.value[highlightedIndex.value]);
	}
}

function handleKeydown(event) {
	if (event.key === 'ArrowDown') {
		event.preventDefault();
		handleArrowKey('down');
	} else if (event.key === 'ArrowUp') {
		event.preventDefault();
		handleArrowKey('up');
	} else if (event.key === 'ArrowRight') {
		event.preventDefault();
		handleArrowKey('right');
	} else if (event.key === 'ArrowLeft') {
		event.preventDefault();
		handleArrowKey('left');
	} else if (event.key === 'Enter') {
		handleEnterKey();
	}
}

function updateFilter(event) {
	if (props.options.length > 1) {
		isOpen.value = true;
	}
	searchTerm.value = event.target.value;
	highlightedIndex.value = -1;
}

const handleClickOutside = event => {
	if (!event.target.closest('.dropdown')) {
		isOpen.value = false;
		searchTerm.value = '';
		highlightedIndex.value = -1;
	}
};

watchEffect(() => {
	const name = selectedItemName.value;
	const icon = selectedItemIcon.value;
	const tag = selectedItemTag.value;
	const optionsCount = props.options.length;

	nextTick(() => {
		calculateRequiredWidth();
	});
});

onMounted(() => {
	document.addEventListener('click', handleClickOutside);
	calculateRequiredWidth();
});

onUnmounted(() => {
	document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="scss" scoped>
.dropdown {
	min-width: v-bind(width);
	position: relative;
	cursor: pointer;
	user-select: none;
	margin: 0 1.5rem 0 0;
}

.dropdown-selected {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 0.5rem;
	background-color: var(--blanc) !important;
	padding: 8px;
	border: 1px solid var(--noir);
	border-radius: 30px;
	color: var(--noir);
	font-weight: 500;
	font-size: 0.8rem;
	width: 100%;
}

.dropdown-item-icon {
	width: 20px;
	height: 20px;
	object-fit: contain;
	margin-left: 0.5rem;
}

.options-item-icon {
	margin-left: 0;
}

.dropdown-filter {
	flex: 1;
	min-width: 0;
	padding: 4px 0 4px 1rem;
	border: none;
	background-color: var(--blanc);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	&::placeholder {
		color: var(--noir);
		font-weight: 500;
		opacity: 1;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&:focus {
		outline: none;
	}
}

.dropdown-selected.has-icon .dropdown-filter {
	padding-left: 0;
}

.dropdown-tag {
	padding: .4rem .75rem;
	border-radius: 1rem;
	font-size: 0.75rem;
	font-weight: 600;
	white-space: nowrap;
	flex-shrink: 0;
	margin-right: .5rem;
	letter-spacing: 0.03rem;
}

.options-tag {
	margin-left: auto;
}

.dropdown-tag-dot {
	width: 12px;
	height: 12px;
	border-radius: 50%;
	flex-shrink: 0;
	margin-right: .5rem;
	padding: 0;
	background-color: #890221;
}

.options-tag-dot {
	margin-left: auto;
}

.item {
	justify-content: space-between;
}

.item-name {
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.dropdown-icon {
	margin-right: 1rem;
	width: 12px;
	height: 12px;
	display: inline-block;
	background-image: url('https://icons.veryicon.com/png/o/miscellaneous/zol-m-station/icon-arrow-up-2.png');
	background-repeat: no-repeat;
	background-position: center;
	transform: rotate(-180deg);
	background-size: contain;
	transition: transform 0.2s ease-in-out;
	flex-shrink: 0;
	vertical-align: middle;
	margin-top: .2rem;
}

.icon-up {
	transform: rotate(0deg);
}

.dropdown-options {
	position: absolute;
	background-color: var(--blanc);
	border: 1px solid var(--gris-100);
	max-height: 260px;
	overflow-y: auto;
	border-radius: 9px;
	width: 100%;
	z-index: 999;
	font-size: .95rem;
}

.dropdown-option-selected {
	background-color: var(--bleu-100);
}

.item {
	padding: 0.3rem 1rem;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	min-width: 0;

	&:hover {
		background-color: var(--bleu-100);
	}
}

.dropdown-option-highlighted {
	background-color: var(--jaune-200);
}
</style>
